import {OFFLINE_MESSAGE} from 'utils/connectionMessages';

/** Which email an {@link EmailLinkform} sends, recorded with any failure. */
export type EmailSendAction = 'passwordReset' | 'signInLink';

const NETWORK_REQUEST_FAILED = 'auth/network-request-failed';

/** The code Firebase Auth gives a disabled account, on sign-in and email requests. */
export const USER_DISABLED_ERROR_CODE = 'auth/user-disabled';

/** What a disabled account is told, also used as its own translation key. */
export const ACCOUNT_DISABLED_MESSAGE =
  'This account has been disabled. For help, email codeinplace@stanford.edu.';

const SIGN_IN_UNREACHABLE_MESSAGE =
  "We couldn't reach the sign-in service. Check your connection or VPN, then try again.";

/** Advice for the Firebase Auth error codes a student can act on. */
const ADVICE_BY_CODE: Readonly<Record<string, string>> = {
  'auth/too-many-requests':
    'Too many tries from this device or network. Wait a few minutes, then try again.',
  [NETWORK_REQUEST_FAILED]: SIGN_IN_UNREACHABLE_MESSAGE,
  'auth/invalid-email':
    'That email address looks incomplete. Check it, then try again.',
  'auth/missing-email': 'Enter your email address.',
  'auth/user-not-found':
    'No account uses that email address. Check it, or sign in another way.',
  'auth/unauthorized-continue-uri':
    "This page's address can't send sign-in emails. Open the course website directly, then try again.",
  'auth/invalid-continue-uri':
    "This page's address can't send sign-in emails. Open the course website directly, then try again.",
  'auth/quota-exceeded': "We can't send email right now. Try again later.",
  [USER_DISABLED_ERROR_CODE]: ACCOUNT_DISABLED_MESSAGE,
};

/**
 * The advice to show when Firebase refuses to send a sign-in or reset email.
 *
 * @param code - The Firebase error's `code`, e.g. `auth/too-many-requests`.
 *     Omitted means the error had none.
 * @return An English message, used as its own translation key, or
 *     `undefined` when the code has no specific advice.
 * @throws Never.
 */
export function emailSendAdvice(code: string | undefined): string | undefined {
  return code === undefined ? undefined : ADVICE_BY_CODE[code];
}

/**
 * The advice to show when a sign-in attempt failed because of the connection.
 * While the browser is offline every failure is put down to that, since
 * nothing else can be fixed until it reconnects.
 *
 * @param params.code - The Firebase error's `code`. Omitted means the error
 *     had none.
 * @param params.isOffline - Whether the browser reports being offline.
 * @return An English message, used as its own translation key, or
 *     `undefined` when the failure was not the connection's.
 * @throws Never.
 */
export function signInConnectionAdvice({
  code,
  isOffline,
}: {
  code?: string;
  isOffline: boolean;
}): string | undefined {
  if (isOffline) {
    return OFFLINE_MESSAGE;
  }
  return code === NETWORK_REQUEST_FAILED
    ? SIGN_IN_UNREACHABLE_MESSAGE
    : undefined;
}

/**
 * The advice to show when a sign-in attempt failed: why a disabled account
 * can't sign in, otherwise the connection advice from
 * {@link signInConnectionAdvice}.
 *
 * @param params.code - The Firebase error's `code`. Omitted means the error
 *     had none.
 * @param params.isOffline - Whether the browser reports being offline.
 * @return An English message, used as its own translation key, or
 *     `undefined` when there is no specific advice.
 * @throws Never.
 */
export function signInFailureAdvice({
  code,
  isOffline,
}: {
  code?: string;
  isOffline: boolean;
}): string | undefined {
  if (code === USER_DISABLED_ERROR_CODE) {
    return ACCOUNT_DISABLED_MESSAGE;
  }
  return signInConnectionAdvice({code, isOffline});
}

/**
 * The `code` of a thrown value, when it has a string one, as Firebase errors
 * do.
 *
 * @param error - What was thrown.
 * @return The code, or `undefined`.
 * @throws Never.
 */
export function errorCodeOf(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return undefined;
  }
  return typeof error.code === 'string' ? error.code : undefined;
}

/**
 * The domain of an email address, for logs that must not hold the address.
 *
 * @param email - What the student typed.
 * @return The lowercased part after the last `@`, or `''` when there is none.
 * @throws Never.
 */
export function emailDomainOf(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1
    ? ''
    : email
        .slice(at + 1)
        .trim()
        .toLowerCase();
}

/**
 * Replaces anything that looks like an email address, so a message can be
 * logged without one.
 *
 * @param text - The text to clean.
 * @return The text with each address replaced by `[email]`.
 * @throws Never.
 */
export function redactEmails(text: string): string {
  return text.replace(/[^\s@()<>"']+@[^\s@()<>"']+/g, '[email]');
}
